/**
 * The command bar's recent items, kept in this browser.
 *
 * Only pages, Settings tabs and named records (a campaign, a file, a
 * segment) are kept, as an id and the label shown for it. Never a contact:
 * their names, emails and numbers are personal data and have no business in
 * localStorage, where anyone at the machine can read them. As a second line,
 * anything that looks like an email address or a phone number is refused
 * whatever it claims to be.
 *
 * The key is new; no existing `hawfinch_*` key is reused or renamed.
 */
import { groupOf } from './commands';

export const RECENT_KEY = 'hawfinch_cmdk_recent';
export const RECENT_MAX = 6;

export type Recent = { id: string; label: string };

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/;
/** Ten or more digits, allowing a single space, dash, dot or bracket between them. */
const PHONE = /(?:\d[\s().+-]?){10,}/;

export function looksPersonal(text: string): boolean {
  return EMAIL.test(text) || PHONE.test(text);
}

/** An item fit to keep, or null. */
export function keepable(item: { id: string; label: string }): Recent | null {
  if (typeof item?.id !== 'string' || typeof item?.label !== 'string') return null;
  if (!groupOf(item.id)) return null; // includes every lead: they have no group here
  const label = item.label.trim().slice(0, 120);
  if (!label || looksPersonal(item.id) || looksPersonal(label)) return null;
  return { id: item.id, label };
}

export function readRecent(): Recent[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((x) => keepable(x as Recent))
      .filter((x): x is Recent => x !== null)
      .slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

/** Puts an item at the top of the list and returns the new list. */
export function pushRecent(item: { id: string; label: string }): Recent[] {
  const kept = keepable(item);
  const current = readRecent();
  if (!kept) return current;
  const next = [kept, ...current.filter((r) => r.id !== kept.id)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* private mode or full storage: the list just is not remembered */
  }
  return next;
}
